import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { Plus, Search, SlidersHorizontal, CheckCircle2, ArrowUp, Monitor, Smartphone } from 'lucide-react';
import { DottedLogo } from '../components/DottedLogo';
import { UseCaseCrossLinks } from '../components/UseCaseCrossLinks';
import { UseCaseTestimonials } from '../components/UseCaseTestimonials';
import { ClosingCTA } from '../components/ClosingCTA';
const TESTIMONIALS = [{
  name: 'Maya Chen',
  role: 'Head of Growth, B2B SaaS',
  avatar: 'https://i.pravatar.cc/100?img=44',
  quote: 'Our Monday reporting used to eat half a day. Now Giga has the channel rollup waiting in Slack before standup.'
}, {
  name: 'Jordan Reyes',
  role: 'Performance Marketing Lead',
  avatar: 'https://i.pravatar.cc/100?img=68',
  quote: 'Giga caught a broad-match campaign burning budget with zero closed deals. That one catch paid for the year.'
}, {
  name: 'Priya Nair',
  role: 'Demand Gen Manager',
  avatar: 'https://i.pravatar.cc/100?img=26',
  quote: 'Attribution finally ties back to revenue instead of last clicks. I walk into pipeline reviews with answers, not guesses.'
}];
const ROTATING_CLAIMS = ['See how your campaigns are doing', 'Put together your weekly report', 'Keep an eye on ad spend', 'Map out your content calendar', "See what's driving your leads", 'Check your SEO rankings', 'Draft your next sprint plan', 'Write up a customer story'];
const FEATURED_APPS = [{
  slug: 'hubspot',
  label: 'HubSpot'
}, {
  slug: 'mixpanel',
  label: 'Mixpanel'
}, {
  slug: 'google-analytics',
  label: 'Google Analytics'
}, {
  slug: 'google-search-console',
  label: 'Search Console'
}];
type Source = {
  slug: string;
  name: string;
  desc: string;
};
type Step = {
  id: string;
  label: string;
  sessionTitle: string;
  prompt: string;
  sources: Source[];
  briefTitle: string;
  briefLines: string[];
};
const STEPS: Step[] = [{
  id: 'campaign-review',
  label: 'Campaign Review',
  sessionTitle: 'Q1 campaign performance review',
  prompt: 'How did our Q1 campaigns perform?',
  sources: [{
    slug: 'google-ads',
    name: 'Google Ads',
    desc: 'Pull spend and conversion data for all campaigns'
  }, {
    slug: 'google-analytics',
    name: 'Google Analytics',
    desc: 'Pull traffic and goal completions by channel'
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    desc: 'Cross-reference leads generated with closed-won revenue'
  }, {
    slug: 'google-sheets',
    name: 'Google Sheets',
    desc: 'Build channel-by-channel performance report'
  }],
  briefTitle: 'Q1 performance report is ready. Here is the headline:',
  briefLines: ['Spend: $184k across 4 channels', 'Pipeline generated: $1.1M (6x ROAS on qualified pipeline)', 'Closed-won revenue from Q1 leads: $312k so far, with another $420k still open']
}, {
  id: 'content-calendar',
  label: 'Content Calendar',
  sessionTitle: 'Q2 content pipeline',
  prompt: 'Where are we blocked on Q2 content?',
  sources: [{
    slug: 'notion',
    name: 'Notion',
    desc: 'Scan the editorial calendar board'
  }, {
    slug: 'slack',
    name: 'Slack',
    desc: 'Check #content-approvals for pending reviews'
  }],
  briefTitle: 'Content pipeline status:',
  briefLines: ['3 blog posts are ready for publishing this week.', 'The Enterprise Whitepaper is blocked waiting on legal review (pinged 2 days ago).', "Next week's webinar deck needs design polish."]
}, {
  id: 'lead-attribution',
  label: 'Lead Attribution',
  sessionTitle: '90-day attribution rollup',
  prompt: 'Which campaigns are actually driving sales?',
  sources: [{
    slug: 'salesforce',
    name: 'Salesforce',
    desc: 'Pull closed-won deals for the last 90 days'
  }, {
    slug: 'google-ads',
    name: 'Google Ads',
    desc: 'Match lead sources back to specific campaigns'
  }, {
    slug: 'google-analytics',
    name: 'Google Analytics',
    desc: 'Attribute multi-touch journeys from first visit to close'
  }],
  briefTitle: 'Here is what is actually working:',
  briefLines: ['Enterprise Demo (paid search): 42% of closed-won revenue, $78 CPA — winner.', 'Retargeting pricing-page visitors: 24% of revenue, $112 CPA.', 'Content syndication/G2: 18% of revenue, $204 CPA, slower but steady.']
}, {
  id: 'ad-monitoring',
  label: 'Ad Monitoring',
  sessionTitle: 'Weekly spend check',
  prompt: 'Are any campaigns burning budget without converting?',
  sources: [{
    slug: 'google-ads',
    name: 'Google Ads',
    desc: 'Check CPA trends over the last 7 days'
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    desc: 'Verify lead quality for high-spend campaigns'
  }],
  briefTitle: '2 campaigns need attention:',
  briefLines: ['Brand search (broad match): Spent $14k, zero closed deals. CPA is 3x higher than average.', 'Paid social (awareness): Spent $8k, 2 meetings booked, 0 closed. Audience is too cold.', 'Want me to draft a pause recommendation?']
}, {
  id: 'sprint-planning',
  label: 'Sprint Planning',
  sessionTitle: 'Marketing Sprint 42',
  prompt: 'Draft our next sprint board based on current priorities',
  sources: [{
    slug: 'linear',
    name: 'Linear',
    desc: 'Pull rolled-over tickets from Sprint 41'
  }, {
    slug: 'slack',
    name: 'Slack',
    desc: 'Extract new requests from #marketing-requests'
  }],
  briefTitle: 'Sprint 42 draft ready:',
  briefLines: ['Carried over: 4 tickets (mostly website copy updates).', 'New: 8 requests from Sales for the upcoming conference.', 'Capacity looks tight — recommend pushing the newsletter redesign to Sprint 43.']
}];
function GeneratingStep({
  icon,
  text,
  index
}: {
  icon: string;
  text: string;
  index: number;
}) {
  const reduce = useReducedMotion();
  const words = text.split(' ');
  return <div className="flex items-start gap-3" data-id="element-9583" data-mp="13zzzpcw9zrdw">
      <div className="relative w-6 h-6 shrink-0 mt-0.5" data-id="element-9584" data-mp="yy291xljpuax">
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: reduce ? 0 : index * 0.25
      }} className="absolute inset-0 rounded bg-white/10 flex items-center justify-center z-10" data-id="element-9585" data-mp="3sr27hjp3jfui">
          <AppIcon slug={icon} className="w-3.5 h-3.5 object-contain" data-id="element-9586" data-mp="3a1devx8pwbib" />
        </motion.div>
        {!reduce && <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} whileInView={{
        opacity: [0, 1, 0],
        scale: [0.8, 1.4, 1.6]
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.8,
        delay: index * 0.25,
        ease: 'easeOut'
      }} className="absolute inset-0 rounded border border-[#EA580C]/40" data-id="element-9587" data-mp="3e1e6kjktuj7b" />}
      </div>

      <p className="text-sm text-neutral-300 leading-relaxed flex flex-wrap gap-x-[0.25em]" data-id="element-9588" data-mp="2vammoo37raeo">
        {words.map((word, wIdx) => <motion.span key={wIdx} initial={{
        opacity: 0,
        y: 4
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.2,
        delay: reduce ? 0 : index * 0.25 + 0.1 + wIdx * 0.02
      }} className="inline-block" data-id="element-9589" data-mp="2nrn58nzbbsxy" data-mpi={wIdx}>
            {word}
          </motion.span>)}
      </p>
    </div>;
}
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  return <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className={className} onError={e => {
    const img = e.currentTarget as HTMLImageElement;
    if (!img.src.includes('simpleicons')) {
      // simpleicons slugs have no hyphens (e.g. googleads, googlesheets)
      img.src = `https://cdn.simpleicons.org/${slug.replace(/-/g, '')}`;
    } else {
      img.style.visibility = 'hidden';
    }
  }} data-id="element-9590" data-mp="200zvry9r0v9a" />;
}
function ProductWindow({
  step,
  device
}: {
  step: Step;
  device: 'desktop' | 'mobile';
}) {
  const isMobile = device === 'mobile';
  return <motion.div layout initial={false} animate={{
    width: isMobile ? 360 : '100%',
    borderRadius: isMobile ? 36 : 16
  }} transition={{
    type: 'spring',
    bounce: 0.2,
    duration: 0.6
  }} className="relative mx-auto bg-[#FAF8F4] border border-[#EAE4D9] shadow-2xl shadow-orange-900/10 overflow-hidden flex flex-col" data-id="element-9591" data-mp="39yzeu22gusxk">
      {/* Phone Notch (only visible in mobile) */}
      <AnimatePresence data-id="element-9592" data-mp="2r1ezpmpi78q5">
        {isMobile && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-50 shadow-sm" data-id="element-9593" data-mp="2r2gb5saflx8e" />}
      </AnimatePresence>

      {/* Giga-branded Header */}
      <div className={`relative flex items-center justify-between px-5 bg-[#1A1410] border-b border-black/20 z-40 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${isMobile ? 'h-14 pt-4' : 'h-12'}`} data-id="element-9594" data-mp="28af4w4vbv4oy">
        <div className="flex items-center gap-2" data-id="element-9595" data-mp="1jq2btgf7hq1z">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-9596" data-mp="1jrdfzhckcztm" />
          <span className="text-xs font-bold text-white tracking-wide" data-id="element-9597" data-mp="22omz0xdz8bac">
            Giga Workspace
          </span>
        </div>
        <div className="flex items-center gap-3 text-neutral-400" data-id="element-9598" data-mp="2hbpxb6a4inyq">
          <div className="w-2 h-2 rounded-full bg-[#EA580C] animate-pulse shadow-[0_0_8px_rgba(234,88,12,0.6)]" data-id="element-9599" data-mp="2cden2ufx2bfz" />
          <span className="text-[10px] font-mono uppercase tracking-widest" data-id="element-9600" data-mp="2qer1h1ffi4zr">
            Active
          </span>
        </div>
      </div>

      <div className="flex h-[460px] md:h-[520px]" data-id="element-9601" data-mp="2lgfo4gpxdccs">
        {/* Sidebar */}
        <AnimatePresence data-id="element-9602" data-mp="2gt7581ud0eq9">
          {!isMobile && <motion.div initial={{
          width: 0,
          opacity: 0
        }} animate={{
          width: 224,
          opacity: 1
        }} exit={{
          width: 0,
          opacity: 0
        }} className="hidden sm:flex shrink-0 flex-col gap-4 border-r border-[#EAE4D9] bg-[#FCFBF9] p-4 z-20" data-id="element-9603" data-mp="2gu8eq1vq8t5v">
              <button className="flex items-center justify-center gap-2 rounded-xl bg-white border border-[#EAE4D9] hover:border-[#D4D0C8] hover:bg-neutral-50 transition-all py-2.5 text-sm font-bold text-[#2B211A] shadow-sm" data-id="element-9604" data-mp="2zqzy0bb9r89x">
                <Plus size={16} className="text-[#EA580C]" data-id="element-9605" data-mp="2ps2bfdxelotk" /> New Session
              </button>
              <div className="flex flex-col gap-1 mt-2" data-id="element-9606" data-mp="2kuscn7o4k0t2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2 px-2" data-id="element-9607" data-mp="2kwd6ev2m7fpv">
                  Recent
                </span>
                {[{
              t: step.sessionTitle,
              time: 'Now',
              active: true
            }, {
              t: 'Bottom-funnel vs top-funnel',
              time: '6h',
              active: false
            }, {
              t: 'Multi-touch attribution audit',
              time: '2d',
              active: false
            }].map((s, i) => <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors cursor-pointer ${s.active ? 'bg-white text-[#2B211A] font-bold border border-[#EAE4D9] shadow-sm' : 'text-neutral-500 hover:bg-white border border-transparent hover:border-[#EAE4D9] hover:shadow-sm'}`} data-id="element-9608" data-mp="3lfokx6e0hlh" data-mpi={i}>
                    <span className="truncate" data-id="element-9609" data-mp="hmi7j1235bs6">{s.t}</span>
                    {s.active && <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C] shrink-0 ml-2" data-id="element-9610" data-mp="ml3bnbkgkflw" />}
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Main panel */}
        <div className="flex-1 flex flex-col bg-[#FAF8F4] relative overflow-hidden shadow-[inset_1px_0_0_rgba(255,255,255,0.5)]" data-id="element-9611" data-mp="2ob50p2fphpah">
          {/* Subtle background texture */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#2B211A 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} data-id="element-9612" data-mp="2c9nae8htb8mj" />

          {/* Chat header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAE4D9] bg-white/80 backdrop-blur-md z-10 shadow-sm" data-id="element-9613" data-mp="2l9k3qnf5cvnx">
            <h2 className="text-base md:text-lg font-title font-bold text-[#2B211A] flex items-center gap-2 truncate" data-id="element-9614" data-mp="2v8hmf9pw5uz0">
              {step.sessionTitle}
            </h2>
            <div className="flex items-center gap-3 text-neutral-400 shrink-0" data-id="element-9615" data-mp="38z4lx43h7ow5">
              <button className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center transition-colors" data-id="element-9616" data-mp="3424fgs2psk4i">
                <Search size={16} className="text-[#2B211A]" data-id="element-9617" data-mp="3ivzihhg6vf7s" />
              </button>
              <button className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center transition-colors" data-id="element-9618" data-mp="3e0k8j0lay3gn">
                <SlidersHorizontal size={16} className="text-[#2B211A]" data-id="element-9619" data-mp="1dk848139ot0" />
              </button>
            </div>
          </div>

          <AnimatedChatFlow step={step} data-id="element-9620" data-mp="3nn4of3jcn5sl" />
        </div>
      </div>
    </motion.div>;
}
function AnimatedChatFlow({
  step
}: {
  step: Step;
}) {
  const reduce = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'typing' | 'submitted' | 'sources' | 'summary'>('typing');
  const [typedChars, setTypedChars] = useState(0);
  const [visibleSources, setVisibleSources] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  // Keep the latest message in view as new content streams in, so the
  // conversation scrolls up inside the fixed window like a real chat.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [phase, visibleSources, visibleLines, reduce]);
  useEffect(() => {
    if (reduce) {
      setPhase('summary');
      setTypedChars(step.prompt.length);
      setVisibleSources(step.sources.length);
      setVisibleLines(step.briefLines.length);
      return;
    }
    // Reset state for new step
    setPhase('typing');
    setTypedChars(0);
    setVisibleSources(0);
    setVisibleLines(0);
    let isCancelled = false;
    const runSequence = async () => {
      // 1. Fast typing
      for (let i = 1; i <= step.prompt.length; i++) {
        if (isCancelled) return;
        setTypedChars(i);
        await new Promise(r => setTimeout(r, 15)); // 15ms per char
      }
      if (isCancelled) return;
      await new Promise(r => setTimeout(r, 200)); // Pause before submit
      // 2. Submit
      if (isCancelled) return;
      setPhase('submitted');
      await new Promise(r => setTimeout(r, 300)); // Pause before sources
      // 3. Stream sources
      if (isCancelled) return;
      setPhase('sources');
      for (let i = 1; i <= step.sources.length; i++) {
        if (isCancelled) return;
        setVisibleSources(i);
        await new Promise(r => setTimeout(r, 150)); // 150ms per source
      }
      if (isCancelled) return;
      await new Promise(r => setTimeout(r, 300)); // Pause before summary
      // 4. Stream summary
      if (isCancelled) return;
      setPhase('summary');
      for (let i = 1; i <= step.briefLines.length; i++) {
        if (isCancelled) return;
        setVisibleLines(i);
        await new Promise(r => setTimeout(r, 150)); // 150ms per line
      }
    };
    runSequence();
    return () => {
      isCancelled = true;
    };
  }, [step, reduce]);
  return <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 z-10 flex flex-col gap-6 md:gap-8 scroll-smooth" data-id="element-9621" data-mp="17bi867ifz3z9">
        {/* User prompt bubble */}
        {phase !== 'typing' && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.2
      }} className="self-end max-w-[85%]" data-id="element-9622" data-mp="2ip45ifyggbvv">
            <div className="inline-flex items-center bg-white border border-[#EAE4D9] shadow-sm px-5 py-3 rounded-2xl rounded-tr-sm" data-id="element-9623" data-mp="dqnm2pjx0fq8">
              <span className="text-[14px] md:text-[15px] text-[#2B211A] leading-snug" data-id="element-9624" data-mp="nm7hlaukhceg">
                {step.prompt}
              </span>
            </div>
          </motion.div>}

        {/* Tool sources */}
        {(phase === 'sources' || phase === 'summary') && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex flex-col gap-2 mb-2" data-id="element-9625" data-mp="3r5aif3h09uam">
            {step.sources.slice(0, visibleSources).map(src => <motion.div key={src.name} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.2
        }} className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-[#EAE4D9] shadow-sm w-fit max-w-[90%]" data-id="element-9626" data-mp="3x6wjxqt9msd">
                <div className="w-8 h-8 rounded-lg bg-neutral-50 flex items-center justify-center border border-neutral-100 shrink-0" data-id="element-9627" data-mp="2y76ngy1g8ttf">
                  <AppIcon slug={src.slug} className="w-4 h-4 object-contain" data-id="element-9628" data-mp="2obmuoki6mb8w" />
                </div>
                <div className="flex flex-col min-w-0 pr-2" data-id="element-9629" data-mp="2t957wn38fcmp">
                  <span className="text-[13px] font-bold text-[#2B211A] truncate" data-id="element-9630" data-mp="2t7lnckajmff4">
                    {src.name}
                  </span>
                  <span className="text-[11px] text-neutral-500 truncate" data-id="element-9631" data-mp="3lyvnz1ofcolg">
                    {src.desc}
                  </span>
                </div>
              </motion.div>)}
          </motion.div>}

        {/* Brief / Summary */}
        {phase === 'summary' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="flex gap-3 md:gap-4 max-w-3xl" data-id="element-9632" data-mp="3c2934cw3lxjf">
            <div className="w-8 h-8 rounded-full bg-[#EA580C] flex items-center justify-center shrink-0 shadow-sm mt-0.5" data-id="element-9633" data-mp="1hugn0fn7xm66">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_saturate(100%)_invert(100%)]" data-id="element-9634" data-mp="1cvdpdg9kii88" />
            </div>
            <div className="flex flex-col gap-4 text-[14px] md:text-[15px] text-[#2B211A] leading-relaxed pt-1" data-id="element-9635" data-mp="20lrb6p1k7kud">
              <p className="font-medium" data-id="element-9636" data-mp="1vlv60mh4lnd8">{step.briefTitle}</p>
              <ul className="space-y-3" data-id="element-9637" data-mp="1qvesl2db21qv">
                {step.briefLines.slice(0, visibleLines).map((line, i) => {
              const parts = line.split(/(\*\*.*?\*\*)/g);
              return <motion.li key={i} initial={{
                opacity: 0,
                x: -5
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.2
              }} className="flex items-start gap-3" data-id="element-9638" data-mp="1mrqkmikrh453" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#EA580C]/40 shrink-0 mt-2" data-id="element-9639" data-mp="147d3ixw62pv" />
                      <div data-id="element-9640" data-mp="61pqpmmut647">
                        {parts.map((part, j) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={j} className="font-bold text-[#2B211A]" data-id="element-9641" data-mp="f2nnbjuyqbcr">
                                {part.slice(2, -2)}
                              </strong>;
                    }
                    return <span key={j} className="text-neutral-700" data-id="element-9642" data-mp="ozs7je58zwqx" data-mpi={j}>
                              {part}
                            </span>;
                  })}
                      </div>
                    </motion.li>;
            })}
              </ul>
            </div>
          </motion.div>}
      </div>

      {/* Chat Composer */}
      <div className="p-4 border-t border-[#EAE4D9] bg-white z-10 shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.02)]" data-id="element-9643" data-mp="12t1d13q2ry46">
        <div className="flex items-center gap-3 bg-white border border-[#EAE4D9] rounded-2xl px-4 py-3 max-w-3xl mx-auto shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus-within:border-[#EA580C] focus-within:ring-1 focus-within:ring-[#EA580C] transition-all" data-id="element-9644" data-mp="17r2y1u72a7ia">
          <button className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200 transition-colors shrink-0" data-id="element-9645" data-mp="15sy9zrnz10e6">
            <Plus size={14} className="text-neutral-500" data-id="element-9646" data-mp="1atvs648zdufo" />
          </button>
          <div className="flex-1 text-[14px] md:text-[15px] text-[#2B211A] font-medium flex items-center h-6" data-id="element-9647" data-mp="1fqvzeihkh38d">
            {phase === 'typing' ? <>
                {step.prompt.substring(0, typedChars)}
                <motion.span animate={{
              opacity: [1, 0]
            }} transition={{
              duration: 0.8,
              repeat: Infinity
            }} className="inline-block w-0.5 h-4 bg-[#EA580C] ml-0.5" data-id="element-9648" data-mp="1okirnymkfoct" />
              </> : <span className="text-neutral-400 font-normal" data-id="element-9649" data-mp="1ygc29cgq57ry">
                Ask Giga anything...
              </span>}
          </div>
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${phase === 'typing' && typedChars > 0 ? 'bg-[#EA580C] text-white shadow-sm' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-9650" data-mp="3nzk9fy9wum32">
            <ArrowUp size={16} strokeWidth={2.5} data-id="element-9651" data-mp="3e1w6il25i89t" />
          </div>
        </div>
      </div>
    </>;
}
export function GrowthMarketing() {
  const [activeStep, setActiveStep] = useState(0);
  const [claimIndex, setClaimIndex] = useState(0);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setClaimIndex(i => (i + 1) % ROTATING_CLAIMS.length);
    }, 2600);
    return () => clearInterval(id);
  }, [reduce]);
  const step = STEPS[activeStep];
  return <>
      <section className="relative w-full bg-[#FAF8F4] text-[#2B211A] overflow-hidden" data-id="element-9652" data-mp="tcggvteo4plr">
        {/* Cream dot-grid texture */}
        <div aria-hidden="true" className="cream-grid z-0" data-id="element-9653" data-mp="oi2kpos6meg1" />

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 pt-32 md:pt-40 pb-20 md:pb-28" data-id="element-9654" data-mp="2itc1for5j5dh">
          {/* Eyebrow */}
          <div className="text-center" data-id="element-9655" data-mp="2nqawc9in3gcq">
            <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-5" data-id="element-9656" data-mp="292praa27f726">
              Growth & Marketing
            </span>

            {/* Headline */}
            <h1 className="font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#2B211A]" data-id="element-9657" data-mp="201rnnsvoh1k4">
              Spend smarter, grow faster
            </h1>

            {/* Rotating claim */}
            <div className="mt-4 h-[1.2em] font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#EA580C]" data-id="element-9658" data-mp="1q4n6y8klpyap">
              <AnimatePresence mode="wait" data-id="element-9659" data-mp="3khminqlv28cp">
                <motion.span key={claimIndex} initial={{
                opacity: 0,
                y: 14
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -14
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} className="block" data-id="element-9660" data-mp="3kio32loro773">
                  {ROTATING_CLAIMS[claimIndex]}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* CTA */}
            <div className="mt-12 flex flex-col items-center gap-4" data-id="element-9661" data-mp="1wjltcjxeauv8">
              <WaitlistCTA className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-9662" data-mp="1wloqrp0vnq9z">
                Get Started
              </WaitlistCTA>
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400" data-id="element-9663" data-mp="21if25z1qlxp2">
                Giga has a 7 day free trial.
              </p>
            </div>

            {/* Featured apps */}
            <div className="mt-16 pt-10 border-t border-[#EAE4D9] max-w-3xl mx-auto" data-id="element-9664" data-mp="75z25fzjbfsn">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-400 mb-6" data-id="element-9665" data-mp="2uc1uhxpf3a68">
                Featured Apps
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-9666" data-mp="2ze0ztd4pwh5t">
                {FEATURED_APPS.map(app => <div key={app.label} className="flex items-center gap-2.5 rounded-xl bg-white border border-[#EAE4D9] px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow" data-id="element-9667" data-mp="3496hfzr379o3">
                    <AppIcon slug={app.slug} className="w-5 h-5 object-contain grayscale opacity-70" data-id="element-9668" data-mp="2bo6ox13yl2o7" />
                    <span className="text-sm font-bold text-[#2B211A]" data-id="element-9669" data-mp="2glyevpl1mepr">
                      {app.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Workflow tabs */}
          <div className="mt-24" data-id="element-9670" data-mp="1swdtl01f35db">
            <div className="flex items-center gap-2 overflow-x-auto border-b border-[#EAE4D9] pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-9671" data-mp="1sv2nuuoevnjk">
              {STEPS.map((s, i) => {
              const isActive = i === activeStep;
              return <button key={s.id} onClick={() => setActiveStep(i)} className={`relative flex items-center gap-2 whitespace-nowrap rounded-lg px-5 py-2.5 text-sm transition-colors ${isActive ? 'text-[#2B211A] font-bold bg-white border border-[#EAE4D9] shadow-sm' : 'text-neutral-500 hover:text-[#2B211A] hover:bg-white/50'}`} data-id="element-9672" data-mp="1pbu6jovxfyk1" data-mpi={i}>
                    {s.label}
                    {isActive && <motion.div layoutId="activeTabIndicator" className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#EA580C]" data-id="element-9673" data-mp="16gnaek0de03t" />}
                  </button>;
            })}

              <div className="ml-auto flex items-center gap-4 pl-4 shrink-0" data-id="element-9674" data-mp="wpsnrej7oyhu">
                {/* Device Toggle */}
                <div className="hidden md:flex items-center bg-white border border-[#EAE4D9] rounded-lg p-1 shadow-sm" data-id="element-9675" data-mp="sm4emrexluuh">
                  <button onClick={() => setDevice('desktop')} className={`p-1.5 rounded-md transition-colors ${device === 'desktop' ? 'bg-[#FAF8F4] text-[#2B211A] shadow-sm border border-[#EAE4D9]' : 'text-neutral-400 hover:text-[#2B211A]'}`} aria-label="Desktop view" data-id="element-9676" data-mp="iq1ets7jy8ap">
                    <Monitor size={16} data-id="element-9677" data-mp="dz5qp6fa9iki" />
                  </button>
                  <button onClick={() => setDevice('mobile')} className={`p-1.5 rounded-md transition-colors ${device === 'mobile' ? 'bg-[#FAF8F4] text-[#2B211A] shadow-sm border border-[#EAE4D9]' : 'text-neutral-400 hover:text-[#2B211A]'}`} aria-label="Mobile view" data-id="element-9678" data-mp="3vank6bx35aqs">
                    <Smartphone size={16} data-id="element-9679" data-mp="ws9moyfz47ia" />
                  </button>
                </div>

                <span className="font-mono text-xs font-bold text-neutral-400" data-id="element-9680" data-mp="i33n39d84f9v">
                  {activeStep + 1} / {STEPS.length}
                </span>
              </div>
            </div>

            {/* Product window */}
            <div className="mt-8" data-id="element-9681" data-mp="1aqgb7nz18v5a">
              <ProductWindow step={step} device={device} data-id="element-9682" data-mp="1apeounckgm89" />
            </div>
          </div>

          {/* Giga Does the Work comparison section */}
          <div className="mt-32 md:mt-40" data-id="element-9683" data-mp="307v3663jigws">
            <div className="text-center mb-20" data-id="element-9684" data-mp="306sfe71ikul3">
              <span className="inline-block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-4" data-id="element-9685" data-mp="2va20hn1g4519">
                Giga does the work
              </span>
              <h2 className="font-title font-bold text-4xl md:text-5xl text-[#2B211A]" data-id="element-9686" data-mp="3a096v4e6hxll">
                Other tools advise.{' '}
                <span className="text-[#EA580C]" data-id="element-9687" data-mp="2h994exvhx1w1">Giga executes.</span>
              </h2>
            </div>

            <div className="space-y-24" data-id="element-9688" data-mp="2cbhbq1n11bqs">
              {/* Row 1: CAMPAIGN REPORTING */}
              <div className="flex flex-col gap-8" data-id="element-9689" data-mp="2qayu9z9qbsrp">
                <div className="text-center max-w-3xl mx-auto" data-id="element-9690" data-mp="2q8cl5v76x57d">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-9691" data-mp="2m7smgn6yxtj4">
                    Campaign Reporting
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-9692" data-mp="1j62et6pkubm">
                    How did our Q1 campaigns perform?
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-9693" data-mp="1zzn9pa7cp5zs">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-9694" data-mp="24yrku6du5dsr">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-9695" data-mp="29rkn8nlu0a1o">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-9696" data-mp="2drlej8u14fq5">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-9697" data-mp="2nqsorn5gvu91">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-9698" data-mp="tdkpnjagkwhl">
                      "Log into Google Ads, export a CSV. Log into Salesforce,
                      export a CSV. Spend 3 hours in Excel trying to match leads
                      to revenue..."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-9699" data-mp="32tu4geuadt0r">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-9700" data-mp="2xs4ul2isvle9" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-9701" data-mp="3uhvyfsczekwq">
                      <div className="flex items-center gap-2" data-id="element-9702" data-mp="3ljjw4t2r5yog">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-9703" data-mp="3lhpbzgzb2alf" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-9704" data-mp="13epcyey36hpj" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-9705" data-mp="17epp1sxpqgtf">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-9706" data-mp="1m45ff7tc0k8l" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-9707" data-mp="1h5u5kx31e9qd" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-9708" data-mp="1v2podh93bfjm">
                      {[{
                      icon: 'google-ads',
                      text: 'Pulling spend and conversion data for Q1'
                    }, {
                      icon: 'salesforce',
                      text: 'Cross-referencing leads with closed-won revenue'
                    }, {
                      icon: 'google-sheets',
                      text: 'Building a channel-by-channel performance report'
                    }, {
                      icon: 'slack',
                      text: 'Sending the summary to the #marketing-leadership channel'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-9709" data-mp="3dydi6b6tl7hm" data-mpi={i} />)}
                    </div>
                    <div className="relative z-10 bg-[#EA580C] rounded-2xl p-6 md:p-8 mt-auto shadow-xl shadow-orange-900/30" data-id="element-9710" data-mp="2z4s6pdu1grm5">
                      <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-3" data-id="element-9711" data-mp="2q2950x1zol1h">
                        The payoff
                      </span>
                      <p className="text-xl md:text-2xl font-bold text-white leading-snug" data-id="element-9712" data-mp="2v2x3g3sjs45q">
                        You get a unified, revenue-tied performance report in
                        seconds, not hours.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: ATTRIBUTION */}
              <div className="flex flex-col gap-8" data-id="element-9713" data-mp="mrw23k8k2ylb">
                <div className="text-center max-w-3xl mx-auto" data-id="element-9714" data-mp="hu4cwxzapqkt">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-9715" data-mp="hwgpswq75ar0">
                    Attribution
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-9716" data-mp="1ahqe5zl1wj4i">
                    Which campaigns are actually driving sales?
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-9717" data-mp="10rdusaoz2vzy">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-9718" data-mp="1t9j57ogk7u9l">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-9719" data-mp="1oj2p1wlctujj">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-9720" data-mp="1vu7gpoxadldd">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-9721" data-mp="25seszugmrf91">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-9722" data-mp="2estgn0h2h6fp">
                      "Look at last-click attribution in Google Analytics and
                      guess which top-of-funnel campaigns actually
                      contributed..."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-9723" data-mp="2tjskjy6yj6sy">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-9724" data-mp="32hw7myxlcbmv" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-9725" data-mp="41cz2jth1z2t">
                      <div className="flex items-center gap-2" data-id="element-9726" data-mp="3v8z9qwelz1t6">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-9727" data-mp="3v6cz2nwf866q" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-9728" data-mp="3cawh87muqmqm" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-9729" data-mp="15hkhwy74gs9r">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-9730" data-mp="10kkbuna9vpin" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-9731" data-mp="18z87em4ofcf4" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-9732" data-mp="z2vfntsoz8n0">
                      {[{
                      icon: 'salesforce',
                      text: 'Pulling all closed-won deals for the last 90 days'
                    }, {
                      icon: 'google-analytics',
                      text: 'Mapping multi-touch journeys for each closed deal'
                    }, {
                      icon: 'google-ads',
                      text: 'Tying revenue back to specific ad groups and keywords'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-9733" data-mp="1myzrn4lsdp1k" data-mpi={i} />)}
                    </div>
                    <div className="relative z-10 bg-[#EA580C] rounded-2xl p-6 md:p-8 mt-auto shadow-xl shadow-orange-900/30" data-id="element-9734" data-mp="2ao27c6wv17tf">
                      <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-3" data-id="element-9735" data-mp="2pclsjpa7jh4y">
                        The payoff
                      </span>
                      <p className="text-xl md:text-2xl font-bold text-white leading-snug" data-id="element-9736" data-mp="2kby3h95jhadd">
                        You see exactly which campaigns drive revenue, not just
                        cheap clicks.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: BUDGET REALLOCATION */}
              <div className="flex flex-col gap-8" data-id="element-9737" data-mp="21lot3bzlmcal">
                <div className="text-center max-w-3xl mx-auto" data-id="element-9738" data-mp="1wnn7okepa0vy">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-9739" data-mp="1wm3syy8ozxwa">
                    Budget Reallocation
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-9740" data-mp="1nmyvlv8vaalp">
                    Are any campaigns burning budget?
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-9741" data-mp="3ubhbazup07ld">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-9742" data-mp="771fukenc6dm">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-9743" data-mp="c5vzfokftyys">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-9744" data-mp="3blj0ibzig0e1">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-9745" data-mp="3km7j7jzumox9">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-9746" data-mp="2rxa1bhzjuu51">
                      "Review your dashboards manually every week to spot
                      campaigns that are burning budget..."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-9747" data-mp="1lar6emdo8szl">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-9748" data-mp="1zb1yttumqfmv" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-9749" data-mp="1v6u5tkh52diu">
                      <div className="flex items-center gap-2" data-id="element-9750" data-mp="295ttaosx0mdf">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-9751" data-mp="2480q84mws8hd" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-9752" data-mp="2mzl2fw2qi48m" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-9753" data-mp="2xkf26xupec3c">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-9754" data-mp="2xmhrtgry237j" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-9755" data-mp="2nuvjptawkk90" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-9756" data-mp="2smwv7d08y6xz">
                      {[{
                      icon: 'google-ads',
                      text: 'Scanning all active campaigns for CPA spikes'
                    }, {
                      icon: 'salesforce',
                      text: 'Checking lead quality for high-spend campaigns'
                    }, {
                      icon: 'gmail',
                      text: 'Drafting a recommendation to pause underperforming ads'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-9757" data-mp="30yferwh2ce2d" data-mpi={i} />)}
                    </div>
                    <div className="relative z-10 bg-[#EA580C] rounded-2xl p-6 md:p-8 mt-auto shadow-xl shadow-orange-900/30" data-id="element-9758" data-mp="2r6hxc7zgopv7">
                      <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-3" data-id="element-9759" data-mp="2w4b5h6kbrty4">
                        The payoff
                      </span>
                      <p className="text-xl md:text-2xl font-bold text-white leading-snug" data-id="element-9760" data-mp="2m7olsl3qj8xo">
                        You stop wasting money on campaigns that don't convert
                        before the month ends.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role-relevant social proof right before the conversion moment */}
      <UseCaseTestimonials testimonials={TESTIMONIALS} data-id="element-9761" data-mp="bcrfg6y5juej" />

      {/* Keep visitors in the funnel: other use cases, then the conversion CTA */}
      <UseCaseCrossLinks current="growth-marketing" data-id="element-9762" data-mp="gkqylhqd9otn" />
      <ClosingCTA data-id="element-9763" data-mp="gi4nj849or6o" />
    </>;
}